import React from "react";
import { Button, Box, CircularProgress, SxProps, Theme } from "@mui/material";

/**
 * Reusable black authentication button component with loading state
 * 
 * @example
 * // Basic usage
 * <BlackAuthButton type="submit" isLoading={isLoading}>
 *   Sign In
 * </BlackAuthButton>
 * 
 * @example
 * // With custom onClick and icon
 * <BlackAuthButton 
 *   onClick={handleSignUp} 
 *   startIcon={<PersonIcon />}
 *   isLoading={isCreatingAccount}
 * >
 *   Create Account
 * </BlackAuthButton>
 * 
 * @example
 * // Custom styling
 * <BlackAuthButton 
 *   onClick={handleAction}
 *   fullWidth={false}
 *   sx={{ mt: 1, mb: 1 }}
 * >
 *   Continue
 * </BlackAuthButton>
 */

interface BlackAuthButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  sx?: SxProps<Theme>;
}

const BlackAuthButton: React.FC<BlackAuthButtonProps> = ({
  children,
  onClick,
  type = "button",
  disabled = false,
  isLoading = false,
  fullWidth = true,
  startIcon,
  sx = {},
}) => {
  return (
    <Button
      fullWidth={fullWidth}
      type={type}
      disabled={disabled || isLoading}
      onClick={onClick}
      sx={{
        mt: 2,
        mb: 3,
        bgcolor: "black",
        color: "white",
        "&:hover": {
          bgcolor: "rgba(0, 0, 0, 0.8)",
        },
        position: "relative",
        minHeight: "42px",
        "& .MuiButton-startIcon": {
          marginRight: isLoading ? 0 : 1,
          visibility: isLoading ? "hidden" : "visible",
        },
        ...sx,
      }}
      startIcon={startIcon}
    >
      <Box
        sx={{
          visibility: isLoading ? "hidden" : "visible",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </Box>
      {isLoading && (
        <CircularProgress
          size={24}
          sx={{
            color: "white",
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: "-12px",
            marginLeft: "-12px",
          }}
        />
      )}
    </Button>
  );
};

export default BlackAuthButton; 